import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { MAT_BOTTOM_SHEET_DATA, MatBottomSheetRef } from '@angular/material/bottom-sheet';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { Catalog } from '@cureskin/api-client/src/server-api';
import { ButtonCtaComponent } from '@components/button-cta/button-cta.component';
import { BottomsheetLayoutComponent } from '@components/bottomsheet-layout/bottomsheet-layout.component';
import { CartService } from '@services/cart-service/cart.service';
import { BroadcastService } from '@services/broadcast-service';
import { EventLoggerService } from '@services/event-logger-service';
import { getProductImage } from 'client/helper/product-helper';

export interface ApprovalBottomSheetPayload {
  product: Catalog;
  navigateToCartOnAdd?: boolean;
  navigateToArticlePage?: boolean;
  section?: string;
}

@Component({
  selector: 'approval-bottomsheet',
  imports: [ButtonCtaComponent, BottomsheetLayoutComponent],
  templateUrl: './approval-bottomsheet.component.html',
})
export class ApprovalBottomsheetComponent implements OnInit, OnDestroy {
  private readonly eventLoggerService = inject(EventLoggerService);
  public readonly data: ApprovalBottomSheetPayload = inject(MAT_BOTTOM_SHEET_DATA);
  bottomSheetRef: MatBottomSheetRef<ApprovalBottomsheetComponent> = inject(MatBottomSheetRef<ApprovalBottomsheetComponent>);
  broadcastService: BroadcastService = inject(BroadcastService);
  cartService: CartService = inject(CartService);
  private readonly router = inject(Router);

  productImage: string | null = null;
  private dismissSubscription: Subscription | null = null;

  ngOnInit(): void {
    this.eventLoggerService.cleverTapEvent('impression', JSON.stringify({
      name: 'add_to_cart_approval_bs',
    }));
    this.productImage = getProductImage(this.data.product);
    this.dismissSubscription = this.broadcastService.on('DISMISS_APPROVAL_BOTTOMSHEET').subscribe((): void => {
      this.bottomSheetRef.dismiss();
    });
  }

  ngOnDestroy(): void {
    this.dismissSubscription?.unsubscribe();
  }

  closeBottomSheet(): void {
    this.bottomSheetRef.dismiss();
  }

  async addToCart(): Promise<void> {
    try {
      this.eventLoggerService.cleverTapEvent('click', JSON.stringify({
        name: 'add_to_cart_approval_bs_add',
      }));
      const result = await this.cartService.addOrRemoveFromCart(
        this.data.product,
        'ADD',
        {
          productsInCart: {},
          section: this.data?.section,
          overrideApprovalCheck: true,
        },
      );
      if (result.success) {
        this.broadcastService.broadcast('REFRESH_SHOP_CART', { productsInCart: result.updatedProductsInCart });
        this.bottomSheetRef.dismiss();
        if (this.data.navigateToCartOnAdd) {
          this.router.navigate(['cart']);
        }
      }
    } catch {
      this.bottomSheetRef.dismiss();
    }
  }

  navigateToArticle(): void {
    this.router.navigate([`/product/${this.data.product.objectId}`], { queryParams: { section: this.data?.section } });
    this.bottomSheetRef.dismiss();
  }
}
